import React, { useState,  } from 'react';
import {
  useGetAdvertisersQuery,
  useGetAdvertisingReportQuery,
  useGetTransactionsQuery,
  useGetCountriesQuery,
  useGetCitiesQuery,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import DateRangePicker from '../../../components/DateRangePicker';
import { useLanguage } from '../../../context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faSackDollar,
  faBullhorn,
  faUserTie,
  faChartLine,
  faSearch,
  faTrophy,
  faRotate,
  faExchangeAlt,
  faBullseye,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';



type Tab = 'accounts' | 'payments' | 'performance';

const PERIOD_OPTIONS = [
  { value: 'last_7_days',  label: 'Last 7 Days'  },
  { value: 'last_30_days', label: 'Last 30 Days'  },
  { value: 'last_90_days', label: 'Last 90 Days'  },
  { value: 'this_year',    label: 'This Year'     },
];

function formatCurrency(n: number, currency = 'AED'): string {
  return `${currency} ${Number(n).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '-';
  const dt = new Date(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${dt.getDate().toString().padStart(2,'0')} ${months[dt.getMonth()]}, ${dt.getFullYear()}`;
}

const STATUS_CLS: Record<string, string> = {
  completed: 'badge-light-success',
  pending:   'badge-light-warning',
  failed:    'badge-light-danger',
  refunded:  'badge-light-danger',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  stripe:           'Stripe',
  'fesilah wallet': 'Fesilah Wallet',
  card:             'Card',
  'apple pay':      'Apple Pay',
  wallet:           'Wallet',
  manual:           'Manual',
};

const PACKAGE_BADGE: Record<string, { bg: string; color: string }> = {
  free:      { bg: 'rgba(100,116,139,0.12)', color: '#475569' },
  basic:     { bg: 'rgba(59,130,246,0.12)',  color: '#2563eb' },
  premium:   { bg: 'rgba(168,85,247,0.12)',  color: '#7c3aed' },
  strategic: { bg: 'rgba(248,168,36,0.15)',  color: '#b45309' },
  distinct:  { bg: 'rgba(57,106,84,0.12)',   color: '#396A54' },
};

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  change?: number | null;
  icon: any;
  iconColor?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub, change, icon, iconColor = '#396A54' }) => (
  <div className="col-6 col-xl-3">
    <div className="card h-100">
      <div className="card-body p-6">
        <div className="d-flex align-items-start justify-content-between mb-4">
          <div>
            {change !== null && change !== undefined ? (
              <span
                className="badge fw-semibold fs-8 d-inline-flex align-items-center gap-1"
                style={{
                  background: change >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                  color: change >= 0 ? '#16a34a' : '#dc2626',
                  width: 'fit-content',
                }}
              >
                <FontAwesomeIcon icon={change >= 0 ? faArrowTrendUp : faArrowTrendDown} style={{ fontSize: 10 }} />
                {change >= 0 ? '+' : ''}{change}%
              </span>
            ) : (
              <span style={{ height: 22, display: 'block' }} />
            )}
          </div>
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{ width: 40, height: 40, background: `${iconColor}18`, flexShrink: 0 }}
          >
            <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: 18 }} />
          </div>
        </div>
        <div className="fw-bold text-dark mb-1" style={{ fontSize: 26, lineHeight: 1 }}>{value}</div>
        <div className="text-muted fw-semibold fs-7">{label}</div>
        {sub && <div className="text-muted fs-8 mt-1">{sub}</div>}
      </div>
    </div>
  </div>
);

const PAGE_SIZE = 20;

/* ─── Accounts Tab ──────────────────────────────────────────────────── */
const AccountsTab: React.FC = () => {
  const { t, lang } = useLanguage();
  const [search,    setSearch]    = useState('');
  const [countryId, setCountryId] = useState('');
  const [cityId,    setCityId]    = useState('');
  const [accStatus, setAccStatus] = useState('');
  const [page,      setPage]      = useState(1);
  const navigate = useNavigate();

  const resetPage = () => setPage(1);

  // Server-side pagination/filtering — the backend returns unique advertisers directly,
  // avoiding a full ads-table scan on every load.
  const advertisersParams: Record<string, any> = { page, per_page: PAGE_SIZE };
  if (search)    advertisersParams.search     = search;
  if (countryId) advertisersParams.country_id = countryId;
  if (cityId)    advertisersParams.city_id    = cityId;
  if (accStatus) advertisersParams.acc_status = accStatus;

  const { data: advertisersData, isLoading: adsLoading } = useGetAdvertisersQuery(advertisersParams);
  const { data: reportData } = useGetAdvertisingReportQuery({ period: 'this_year' });
  const { data: countriesData } = useGetCountriesQuery({ per_page: 200 });
  const { data: citiesData }    = useGetCitiesQuery(
    countryId ? { country_id: countryId, per_page: 500 } : { per_page: 500 }
  );

  const countries: any[] = countriesData?.data?.data || countriesData?.data || [];
  const cities: any[]    = citiesData?.data?.data    || citiesData?.data    || [];
  const report = reportData?.data || {};

  const meta        = advertisersData?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: PAGE_SIZE };
  const pageSlice: any[] = meta.data || [];
  const totalCount  = meta.total ?? pageSlice.length;
  const totalPages  = meta.last_page ?? Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage    = meta.current_page ?? page;

  const hasFilters = search || countryId || cityId || accStatus;

  return (
    <>
      {/* Stat Cards */}
      <div className="row g-5 mb-6">
        <StatCard
          label={t('Total Advertisers')}
          value={adsLoading ? '…' : String(totalCount)}
          sub={t('unique users who posted at least 1 ad')}
          icon={faUserTie}
          iconColor="#396A54"
        />
        <StatCard
          label={t('Active Campaigns')}
          value={String(report.active_campaigns ?? 0)}
          change={report.active_campaigns_change ?? null}
          icon={faBullhorn}
          iconColor="#3b82f6"
        />
        <StatCard
          label={t('Total Ad Revenue (This Year)')}
          value={`AED ${Number(report.total_revenue ?? 0).toLocaleString('en', { maximumFractionDigits: 0 })}`}
          change={report.total_revenue_change ?? null}
          icon={faSackDollar}
          iconColor="#22c55e"
        />
        <StatCard
          label={t('Avg. Spend / Advertiser')}
          value={`AED ${Number(report.avg_spending ?? 0).toLocaleString('en', { maximumFractionDigits: 0 })}`}
          change={report.avg_spending_change ?? null}
          icon={faChartLine}
          iconColor="#8b5cf6"
        />
      </div>

      <div className="card">
        <div className="card-header border-0 pt-5 pb-3">
          <div className="d-flex align-items-center flex-wrap gap-3 w-100">

            {/* Search */}
            <div className="d-flex align-items-center position-relative" style={{ flex: '1 1 200px', maxWidth: 280 }}>
              <span className="position-absolute ms-3" style={{ color: '#a1a5b7' }}>
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <input
                type="text"
                className="form-control form-control-solid ps-10"
                placeholder={t('Search name, email…')}
                value={search}
                onChange={(e) => { setSearch(e.target.value); resetPage(); }}
              />
            </div>

            {/* Account Status */}
            <select
              className="form-select form-select-solid"
              style={{ width: 140, flex: '0 0 140px' }}
              value={accStatus}
              onChange={(e) => { setAccStatus(e.target.value); resetPage(); }}
            >
              <option value="">{t('All Statuses')}</option>
              <option value="1">{t('Active')}</option>
              <option value="0">{t('Suspended')}</option>
            </select>

            {/* Country */}
            <select
              className="form-select form-select-solid"
              style={{ width: 160, flex: '0 0 160px' }}
              value={countryId}
              onChange={(e) => { setCountryId(e.target.value); setCityId(''); resetPage(); }}
            >
              <option value="">{t('All Countries')}</option>
              {countries.map((c: any) => (
                <option key={c.id} value={c.id}>{(lang === 'ar' ? c.name_ar : c.name) || c.name}</option>
              ))}
            </select>

            {/* City */}
            <select
              className="form-select form-select-solid"
              style={{ width: 150, flex: '0 0 150px' }}
              value={cityId}
              onChange={(e) => { setCityId(e.target.value); resetPage(); }}
            >
              <option value="">{t('All Cities')}</option>
              {cities.map((c: any) => (
                <option key={c.id} value={c.id}>{(lang === 'ar' ? c.name_ar : c.name) || c.name}</option>
              ))}
            </select>

            {hasFilters && (
              <button
                className="btn btn-sm btn-light-danger"
                onClick={() => { setSearch(''); setCountryId(''); setCityId(''); setAccStatus(''); setPage(1); }}
              >
                <i className="fas fa-times me-1"></i>{t('Clear')}
              </button>
            )}
          </div>
        </div>

        <div className="card-body pt-0">
          {adsLoading ? (
            <PageLoader />
          ) : (
            <>
              <div className="text-muted fs-7 mb-3 mt-4">
                {t('Total:')} <strong>{totalCount}</strong> {t('unique advertisers')}
              </div>
              <div className="table-responsive">
                <table className="table align-middle table-hover fs-6 gy-4">
                  <thead>
                    <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                      <th className="min-w-40px">#</th>
                      <th className="min-w-160px">{t('Advertiser')}</th>
                      <th className="min-w-120px">{t('Country / City')}</th>
                      <th className="min-w-80px text-center">{t('Total Ads')}</th>
                      <th className="min-w-80px text-center">{t('Active Ads')}</th>
                      <th className="min-w-120px">{t('Total Paid')}</th>
                      <th className="min-w-90px">{t('Status')}</th>
                      <th className="min-w-100px">{t('Joined')}</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600 fw-semibold">
                    {pageSlice.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-12">{t('No advertisers found')}</td>
                      </tr>
                    ) : (
                      pageSlice.map((adv: any, idx: number) => (
                        <tr key={adv.id} className="cursor-pointer" onClick={()=> navigate(`/admin/users/${adv.id}`)}>
                          <td className="text-muted fs-7">{(safePage - 1) * PAGE_SIZE + idx + 1}</td>
                          <td>
                            <div className="fw-bold text-gray-800">{adv.name || '-'}</div>
                            <div className="text-muted fs-7">{adv.email || ''}</div>
                            {adv.phone && <div className="text-muted fs-8">{adv.phone}</div>}
                          </td>
                          <td>
                            <div className="text-gray-700">{(lang === 'ar' ? adv.country?.name_ar : adv.country?.name) || adv.country?.name || '-'}</div>
                            <div className="text-muted fs-7">{(lang === 'ar' ? adv.city?.name_ar : adv.city?.name) || adv.city?.name || ''}</div>
                          </td>
                          <td className="text-center">
                            <span className="fw-bold text-dark">{adv.totalAds ?? adv.total_ads ?? 0}</span>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-light-success fw-semibold">{adv.activeAds ?? adv.active_ads ?? 0}</span>
                          </td>
                          <td>
                            <span className="fw-bold text-dark">
                              {(() => {
                                const paid = Number(adv.totalPaid ?? adv.total_paid ?? 0);
                                return paid > 0 ? `AED ${paid.toFixed(2)}` : '—';
                              })()}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${(adv.accStatus ?? adv.acc_status) === 1 ? 'badge-light-success' : 'badge-light-danger'}`}>
                              {(adv.accStatus ?? adv.acc_status) === 1 ? t('Active') : t('Suspended')}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted fs-7">{formatDate(adv.created_at)}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={safePage}
                lastPage={totalPages}
                total={totalCount}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

/* ─── Payments Tab ──────────────────────────────────────────────────── */
const PaymentsTab: React.FC = () => {
  const { t } = useLanguage();
  const [search,        setSearch]        = useState('');
  const [status,        setStatus]        = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [daterange,     setDaterange]     = useState('');
  const [page,          setPage]          = useState(1);

  const resetPage = () => setPage(1);

  const params: Record<string, any> = { page, type: 'ad_payment' };
  if (search)        params.search         = search;
  if (status)        params.status         = status;
  if (paymentMethod) params.payment_method = paymentMethod;
  if (daterange)     params.daterange      = daterange;

  const { data, isLoading } = useGetTransactionsQuery(params);

  const txns = data?.data?.data || [];
  const meta = data?.data || { current_page: 1, last_page: 1, total: 0, per_page: 20 };
  const hasFilters = search || status || paymentMethod || daterange;

  return (
    <div className="card">
      <div className="card-header border-0 pt-5 pb-3">
        <div className="d-flex align-items-center flex-wrap gap-3 w-100">

          <div className="d-flex align-items-center position-relative" style={{ flex: '1 1 200px', maxWidth: 280 }}>
            <span className="position-absolute ms-3" style={{ color: '#a1a5b7' }}>
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              className="form-control form-control-solid ps-10"
              placeholder={t('Search advertiser…')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            />
          </div>

          <select
            className="form-select form-select-solid"
            style={{ width: 140, flex: '0 0 140px' }}
            value={status}
            onChange={(e) => { setStatus(e.target.value); resetPage(); }}
          >
            <option value="">{t('All Statuses')}</option>
            <option value="completed">{t('Completed')}</option>
            <option value="pending">{t('Pending')}</option>
            <option value="failed">{t('Failed')}</option>
            <option value="refunded">{t('Refunded')}</option>
          </select>

          <select
            className="form-select form-select-solid"
            style={{ width: 160, flex: '0 0 160px' }}
            value={paymentMethod}
            onChange={(e) => { setPaymentMethod(e.target.value); resetPage(); }}
          >
            <option value="">{t('All Methods')}</option>
            <option value="Stripe">{t('Stripe')}</option>
            <option value="Fesilah Wallet">{t('Fesilah Wallet')}</option>
            <option value="Card">{t('Card')}</option>
            <option value="Apple Pay">{t('Apple Pay')}</option>
            <option value="wallet">{t('Wallet')}</option>
            <option value="manual">{t('Manual')}</option>
          </select>

          <div style={{ flex: '0 0 220px' }}>
            <DateRangePicker
              value={daterange}
              onChange={(v) => { setDaterange(v); resetPage(); }}
              placeholder={t('Payment Date Range')}
            />
          </div>

          {hasFilters && (
            <button
              className="btn btn-sm btn-light-danger"
              onClick={() => { setSearch(''); setStatus(''); setPaymentMethod(''); setDaterange(''); setPage(1); }}
            >
              <i className="fas fa-times me-1"></i>{t('Clear')}
            </button>
          )}
        </div>
      </div>

      <div className="card-body pt-0">
        {isLoading ? (
          <PageLoader />
        ) : (
          <>
            <div className="text-muted fs-7 mb-3 mt-4">
              {t('Total:')} <strong>{meta.total || 0}</strong> {t('ad payment records')}
            </div>
            <div className="table-responsive">
              <table className="table align-middle table-row-dashed fs-6 gy-4">
                <thead>
                  <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                    <th className="min-w-40px">#</th>
                    <th className="min-w-160px">{t('Advertiser')}</th>
                    <th className="min-w-110px">{t('Amount')}</th>
                    <th className="min-w-110px">{t('Method')}</th>
                    <th className="min-w-100px">{t('Status')}</th>
                    <th className="min-w-100px">{t('Date')}</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 fw-semibold">
                  {txns.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-12">{t('No payment records found')}</td>
                    </tr>
                  ) : (
                    txns.map((txn: any) => (
                      <tr key={txn.id}>
                        <td className="text-muted fs-7">{txn.id}</td>
                        <td>
                          <div className="fw-bold text-gray-800">{txn.user?.name || '-'}</div>
                          <div className="text-muted fs-7">{txn.user?.email || ''}</div>
                        </td>
                        
                        <td>
                          <span className="fw-bold text-success">
                            {txn.currency} {Number(txn.amount).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted text-capitalize">
                            {t(PAYMENT_METHOD_LABELS[txn.payment_method?.toLowerCase()] || txn.payment_method || '-')}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${STATUS_CLS[txn.status] || 'badge-light-secondary'} text-capitalize`}>
                            {txn.status ? t(txn.status.charAt(0).toUpperCase() + txn.status.slice(1)) : '-'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted fs-7">{formatDate(txn.created_at)}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={meta.current_page || 1}
              lastPage={meta.last_page || 1}
              total={meta.total || 0}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

/* ─── Performance Tab ───────────────────────────────────────────────── */
const PerformanceTab: React.FC = () => {
  const { t, lang } = useLanguage();
  const [countryId, setCountryId] = useState('');
  const [cityId,    setCityId]    = useState('');
  const [period,    setPeriod]    = useState('last_30_days');

  const params: Record<string, any> = { period };
  if (countryId) params.country_id = countryId;
  if (cityId)    params.city_id    = cityId;

  const { data, isLoading } = useGetAdvertisingReportQuery(params);
  const { data: countriesData } = useGetCountriesQuery({ per_page: 200 });
  const { data: citiesData }    = useGetCitiesQuery(
    countryId ? { country_id: countryId, per_page: 500 } : { per_page: 500 }
  );

  const countries: any[] = countriesData?.data?.data || countriesData?.data || [];
  const cities: any[]    = citiesData?.data?.data    || citiesData?.data    || [];

  const d            = data?.data || {};
  const topSpenders: Array<{ name: string; country: string; total_spending: number; package: string }> = d.top_spenders || [];

  const summaryStats = [
    { label: t('Active Campaigns'),       value: d.active_campaigns   ?? 0, change: d.active_campaigns_change   ?? null, icon: faBullhorn,    color: '#396A54' },
    { label: t('Total Revenue'),          value: null,  currency: d.total_revenue   ?? 0, change: d.total_revenue_change   ?? null, icon: faSackDollar,  color: '#22c55e' },
    { label: t('Avg. Spend / Advertiser'),value: null,  currency: d.avg_spending    ?? 0, change: d.avg_spending_change    ?? null, icon: faChartLine,   color: '#8b5cf6' },
    { label: t('Free → Paid Rate'),       value: `${d.free_to_paid_rate ?? 0}%`,     change: d.free_to_paid_change   ?? null, icon: faExchangeAlt, color: '#3b82f6' },
    { label: t('Package Renewal Rate'),   value: `${d.renewal_rate      ?? 0}%`,     change: d.renewal_rate_change   ?? null, icon: faRotate,      color: '#f59e0b' },
    { label: t('Average CTR'),            value: `${d.avg_ctr           ?? 0}%`,     change: d.avg_ctr_change        ?? null, icon: faBullseye,    color: '#ef4444' },
  ];

  const packageBadge = (name: string) => {
    const key = name.toLowerCase().replace(/\s+/g, '');
    const style = Object.entries(PACKAGE_BADGE).find(([k]) => key.includes(k))?.[1]
      ?? { bg: 'rgba(57,106,84,0.12)', color: '#396A54' };
    return (
      <span className="badge fw-semibold fs-8 text-capitalize" style={{ background: style.bg, color: style.color }}>
        {t(name)}
      </span>
    );
  };

  return (
    <>
      {/* Filters */}
      <div className="card mb-6">
        <div className="card-body py-5">
          <div className="row g-4 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('Period')}</label>
              <select
                className="form-select form-select-solid"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                {PERIOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{t(o.label)}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('Country')}</label>
              <select
                className="form-select form-select-solid"
                value={countryId}
                onChange={(e) => { setCountryId(e.target.value); setCityId(''); }}
              >
                <option value="">{t('All Countries')}</option>
                {countries.map((c: any) => (
                  <option key={c.id} value={c.id}>{(lang === 'ar' ? c.name_ar : c.name) || c.name}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('City')}</label>
              <select
                className="form-select form-select-solid"
                value={cityId}
                onChange={(e) => setCityId(e.target.value)}
              >
                <option value="">{t('All Cities')}</option>
                {cities.map((c: any) => (
                  <option key={c.id} value={c.id}>{(lang === 'ar' ? c.name_ar : c.name) || c.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : (
        <>
          {/* Summary Stat Cards */}
          <div className="row g-5 mb-6">
            {summaryStats.map((s) => (
              <div key={s.label} className="col-6 col-xl-2">
                <div className="card h-100">
                  <div className="card-body p-5">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded"
                        style={{ width: 36, height: 36, background: `${s.color}18`, flexShrink: 0 }}
                      >
                        <FontAwesomeIcon icon={s.icon} style={{ color: s.color, fontSize: 16 }} />
                      </div>
                      {s.change !== null && s.change !== undefined && (
                        <span
                          className="badge fw-semibold fs-9 d-inline-flex align-items-center gap-1"
                          style={{
                            background: s.change >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                            color: s.change >= 0 ? '#16a34a' : '#dc2626',
                          }}
                        >
                          <FontAwesomeIcon icon={s.change >= 0 ? faArrowTrendUp : faArrowTrendDown} style={{ fontSize: 9 }} />
                          {s.change >= 0 ? '+' : ''}{s.change}%
                        </span>
                      )}
                    </div>
                    <div className="fw-bold text-dark" style={{ fontSize: 20, lineHeight: 1.1 }}>
                      {'currency' in s
                        ? `AED ${Number(s.currency).toLocaleString('en', { maximumFractionDigits: 0 })}`
                        : s.value}
                    </div>
                    <div className="text-muted fw-semibold fs-8 mt-1">{s.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Top Spenders Table */}
          <div className="card">
            <div className="card-header pt-5 border-bottom-0">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faTrophy} style={{ color: '#f6c000' }} />
                  {t('Top 10 Highest-Spending Advertisers')}
                </span>
                <span className="text-muted mt-1 fw-semibold fs-7">
                  {t('Performance report for the selected period')}
                </span>
              </h3>
            </div>
            <div className="card-body pt-2">
              {topSpenders.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                    <thead>
                      <tr className="border-0">
                        <th className="p-0 w-40px text-end text-muted fw-semibold fs-7 text-uppercase">#</th>
                        <th className="p-0 ps-4 text-muted fw-semibold fs-7 text-uppercase">{t('Advertiser')}</th>
                        <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Country')}</th>
                        <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Total Spending')}</th>
                        <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Package')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSpenders.map((s, i) => (
                        <tr key={i}>
                          <td className="text-end pe-4">
                            <span
                              className="fw-bold fs-5"
                              style={{ color: i === 0 ? '#f6c000' : i === 1 ? '#9ca3af' : i === 2 ? '#b45309' : '#3f4254' }}
                            >
                              {i + 1}
                            </span>
                          </td>
                          <td className="ps-4">
                            <span className="text-dark fw-bold fs-6">{s.name}</span>
                          </td>
                          <td>
                            <span className="text-muted fw-semibold fs-7">{s.country}</span>
                          </td>
                          <td>
                            <span className="text-dark fw-bold fs-6">
                              AED {Number(s.total_spending).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td>{packageBadge(s.package)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center py-16">
                  <span className="text-muted fs-7">{t('No performance data available for the selected period')}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

/* ─── Main Page ─────────────────────────────────────────────────────── */
const AdminAdvertiserManagement: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<Tab>('accounts');

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'accounts',    label: t('Accounts'),       icon: 'fas fa-users'       },
    { id: 'payments',    label: t('Payment Records'), icon: 'fas fa-credit-card' },
    { id: 'performance', label: t('Performance'),     icon: 'fas fa-chart-line'  },
  ];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Advertiser Management')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted">{t('Home')}</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Advertiser Management')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">

            {/* Tab Bar */}
            <div className="d-flex gap-2 mb-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`btn rounded-pill fw-semibold ${activeTab === tab.id ? 'btn-base' : 'btn-secondary'}`}
                  style={activeTab === tab.id ? { background: '#396A54', borderColor: '#396A54' } : {}}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <i className={`${tab.icon} me-2`}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'accounts'    && <AccountsTab />}
            {activeTab === 'payments'    && <PaymentsTab />}
            {activeTab === 'performance' && <PerformanceTab />}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdvertiserManagement;
